import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const BuildingAliasesTab = ({ propertyId }) => {
  const { t } = useT();
  const qc = useQueryClient();
  const [alias, setAlias] = useState("");
  const [saving, setSaving] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["building-aliases", propertyId],
    queryFn: async () => (await api.get(`/intake/buildings/${propertyId}/aliases`)).data,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["building-aliases", propertyId] });

  const add = async () => {
    if (!alias.trim()) return;
    setSaving(true);
    try {
      await api.post(`/intake/buildings/${propertyId}/aliases`, { alias: alias.trim() });
      setAlias("");
      refresh();
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/intake/aliases/${id}`);
      refresh();
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    }
  };

  const items = data?.items || [];

  return (
    <section className="rounded-[8px] border border-line bg-white p-4">
      <h2 className="mb-1 flex items-center gap-1.5 text-[13px] font-semibold text-ink">
        <Sparkles className="h-3.5 w-3.5 text-brand" /> {t("intake.aliases.title")}
      </h2>
      <p className="mb-3 text-[12px] text-ink-tertiary">{t("intake.aliases.subtitle")}</p>

      <div className="mb-3 flex gap-2">
        <Input
          data-testid="intake-alias-input"
          value={alias}
          onChange={(e) => setAlias(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder={t("intake.aliases.input_ph")}
          className="h-9 flex-1 rounded-[6px] text-[13px]"
        />
        <Button data-testid="intake-alias-add" disabled={saving} onClick={add} className="h-9 rounded-[6px] text-[13px]">
          <Plus className="mr-1 h-3.5 w-3.5" /> {t("app.add")}
        </Button>
      </div>

      {isLoading ? null : !items.length ? (
        <EmptyState icon={Sparkles} testId="intake-aliases-empty" title={t("intake.aliases.empty_title")}
          description={t("intake.aliases.empty_desc")} />
      ) : (
        <ul className="divide-y divide-line" data-testid="intake-aliases-list">
          {items.map((a) => (
            <li key={a.id} data-testid={`intake-alias-row-${a.id}`} className="flex items-center justify-between py-2">
              <span className="text-[13px] text-ink">{a.alias}</span>
              <button data-testid={`intake-alias-remove-${a.id}`} onClick={() => remove(a.id)}
                className="text-ink-tertiary hover:text-state-critical">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
